import { motion } from "framer-motion";
import { useState } from "react";

import type { AgentRegistryMode, ReportIndexEntry } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface RunLauncherProps {
  /** The agent's dispatch modes, from the registry. */
  modes: AgentRegistryMode[];
  /** Workflow file name, shown as the launcher's source label. */
  workflow: string;
  weeks: ReportIndexEntry[];
  busy: boolean;
  onLaunch: (mode: string, week: string, priorities: string) => void;
}

// Colour per known mode id; anything the registry adds later falls back to the
// neutral tile style rather than needing a code change here.
const HUE_BY_ID: Record<string, string> = {
  plan: "border-primary/50 bg-primary/10 text-primary",
  seo_fixer: "border-magenta/50 bg-magenta/10 text-magenta",
  content_autopilot: "border-violet/50 bg-violet/10 text-violet",
};

export function RunLauncher({ modes, workflow, weeks, busy, onLaunch }: RunLauncherProps) {
  const [mode, setMode] = useState<string>(modes[0]?.id ?? "");
  const [week, setWeek] = useState("latest");
  const [priorities, setPriorities] = useState("1,2");

  const active = modes.find((m) => m.id === mode) ?? modes[0];
  const needsPriorities = mode !== "content_autopilot";

  if (modes.length === 0 || !active) {
    return (
      <div className="surface p-4 space-y-2">
        <p className="path-chip">launch</p>
        <p className="text-xs text-muted/80 leading-relaxed">
          Este agente no se dispara desde el console — corre en{" "}
          <span className="font-mono text-muted">{workflow}</span> por su propio flujo.
        </p>
      </div>
    );
  }

  return (
    <div className="surface p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="path-chip">launch</p>
        <span className="font-mono text-xs text-muted">{workflow}</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={cn(
              "px-2 py-2 rounded border font-mono text-[11px] uppercase tracking-wider transition-all",
              mode === m.id
                ? HUE_BY_ID[m.id] ?? "border-primary/50 bg-primary/10 text-primary"
                : "border-border text-muted hover:text-text hover:border-violet/40",
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      <motion.p
        key={mode}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xs text-muted/90 leading-relaxed min-h-[2.5rem]"
      >
        {active.blurb}
      </motion.p>

      <div className="space-y-3">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
            reporte
          </span>
          <select
            value={week}
            onChange={(e) => setWeek(e.target.value)}
            className="mt-1 w-full bg-bg-elev/60 border border-border rounded px-2 py-1.5 font-mono text-sm text-text focus:border-primary/60 focus:outline-none"
          >
            <option value="latest">latest</option>
            {weeks.map((w) => (
              <option key={w.week} value={w.week}>
                {w.week} · {w.action_count} acciones
              </option>
            ))}
          </select>
        </label>

        {needsPriorities && (
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
              prioridades
            </span>
            <div className="mt-1 flex gap-2">
              {["1", "1,2", "1,2,3"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriorities(p)}
                  className={cn(
                    "flex-1 px-2 py-1.5 rounded border font-mono text-xs transition-colors",
                    priorities === p
                      ? "border-primary/60 bg-primary/10 text-primary"
                      : "border-border text-muted hover:text-text",
                  )}
                >
                  P{p.replace(/,/g, "+P")}
                </button>
              ))}
            </div>
          </label>
        )}
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={() => onLaunch(mode, week, priorities)}
        className={cn(
          "w-full py-2.5 rounded font-mono text-sm uppercase tracking-wider border transition-all",
          busy
            ? "border-border text-muted cursor-wait"
            : "border-primary/60 bg-primary/10 text-primary hover:bg-primary/20 hover:shadow-[0_0_20px_rgba(0,212,255,0.25)]",
        )}
      >
        {busy ? "dispatching…" : `run ${active.label}`}
      </button>

      <p className="text-[11px] text-muted/70 leading-relaxed">
        El agente corre en GitHub Actions, no acá. La corrida aparece abajo cuando
        el console la ve — puede tardar hasta 2 minutos.
      </p>
    </div>
  );
}
